/* ============================================================
   materialStockView — MS-1 (TASK-52194f26): pure view model for
   the provider cabinet «Склад» tab. Derives from stock rows /
   editor draft state only:
   - formatGrams: g → «850 г» / «1,2 кг» (ru-RU, one decimal)
   - isLowStock: badge flag mirror of the backend rule
   - draftItem/editable-row helpers used by the panel + tests

   No fake data; no API calls. Light theme, Montserrat, iOS-18
   minimal per DESIGN-SYSTEM.md.
   ============================================================ */

import type { MaterialStockItem, MaterialStockRow } from '../types/provider';

/** Grams below 1000 → whole grams; ≥ 1000 → kg with one decimal, ru-RU comma. */
export function formatGrams(grams: number): string {
  const g = Number.isFinite(grams) ? grams : 0;
  if (g < 1000) return `${Math.round(g)} г`;
  return `${(g / 1000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} кг`;
}

/** Backend rule mirror: threshold > 0 && remaining <= threshold. */
export function isLowStock(row: Pick<MaterialStockRow, 'remainingGrams' | 'lowStockThresholdGrams'>): boolean {
  return row.lowStockThresholdGrams > 0 && row.remainingGrams <= row.lowStockThresholdGrams;
}

export interface StockDraft {
  materialId: string;
  color: string;
  /** ecfe2257: #RRGGBB (picked via the native color input) or '' — no swatch. */
  hexColor: string;
  remainingGrams: string;
  lowStockThresholdGrams: string;
}

export const EMPTY_DRAFT: StockDraft = {
  materialId: '',
  color: '',
  hexColor: '',
  remainingGrams: '',
  lowStockThresholdGrams: '',
};

/**
 * Parse a draft into a save item. Returns an error string (ru) when the
 * row is incomplete/invalid; grams fields accept comma decimals.
 */
export function draftToItem(
  draft: StockDraft,
): { item: MaterialStockItem } | { error: string } {
  const color = draft.color.trim();
  if (!draft.materialId) return { error: 'Выберите материал' };
  if (color.length < 1 || color.length > 64) return { error: 'Цвет: 1–64 символа' };

  const remaining = parseGrams(draft.remainingGrams);
  if (remaining === null || remaining < 0 || remaining > 10_000_000)
    return { error: 'Остаток: число от 0 до 10 000 000 г' };

  const threshold = parseGrams(draft.lowStockThresholdGrams);
  if (threshold === null || threshold < 0 || threshold > 10_000_000)
    return { error: 'Порог «мало»: число от 0 до 10 000 000 г' };

  const hex = (draft.hexColor ?? '').trim();
  if (hex !== '' && !/^#[0-9A-Fa-f]{6}$/.test(hex))
    return { error: 'Hex цвета: формат #RRGGBB или пусто' };

  return {
    item: {
      materialId: draft.materialId,
      color,
      hexColor: hex === '' ? null : hex.toUpperCase(),
      remainingGrams: remaining,
      lowStockThresholdGrams: threshold,
    },
  };
}

function parseGrams(input: string): number | null {
  const text = input.trim().replace(',', '.');
  if (text === '') return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/** Case-insensitive (materialId, color) duplicate check across a save list. */
export function hasDuplicateColors(items: MaterialStockItem[]): boolean {
  const seen = new Set<string>();
  for (const item of items) {
    const key = `${item.materialId}|${item.color.trim().toLowerCase()}`;
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
}

/** Total distinct colors in stock (remaining > 0) — the tab's summary line. */
export function inStockColorCount(rows: MaterialStockRow[]): number {
  return rows.filter((r) => r.remainingGrams > 0).length;
}
